// The AppBar every Flutter device screen shares: avatar, device name, a
// "Location > Room" subtitle, a pencil that renames the device, and the ⋮ menu
// from allDeviceFunctions.dart (DeviceMenuButton).
//
// The Dart menu publishes `getallwifi` before opening any WiFi entry so the
// device's list is fresh by the time the sheet appears; the same publish happens
// here. WiFi entries reuse DeviceWifiPanel, which already speaks that protocol.

import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Button, Typography, Box, useTheme,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import InfoIcon from '@mui/icons-material/Info';
import SignalWifi4BarIcon from '@mui/icons-material/SignalWifi4Bar';
import SignalWifiBadSharpIcon from '@mui/icons-material/SignalWifiBadSharp';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import LockIcon from '@mui/icons-material/Lock';
import TuneIcon from '@mui/icons-material/Tune';
import UpdateCodeDialog from '../../../../shared/UpdateCodeDialog';
import ButtonSettingsDialog from '../../../../shared/ButtonSettingsDialog';
import { ProfileTitleBar } from '../../../components/MobileAppBar';
import { useMessage } from '../../../components/MessageDialog';
import { useApp } from '../../../../../core/state/AppContext';
import { renameDeviceInstalled, getDeviceDetail, getDeviceInstalledById } from '../../../../../core/api/api';
import { publishCommand, publishAddressed } from '../../../../../core/utils/deviceController';
import { deviceLabel } from '../../../../../core/devices/deviceMeta';
import { CMD_GET_ALL_WIFI, buildAddWifiMqtt } from '../../../../../core/devices/commands';
import WifiPickerDialog from '../../../components/WifiPickerDialog';
import WifiRemoveDialog from '../../../components/WifiRemoveDialog';
import ChildLockPanel from '../../../../shared/ChildLockPanel';
import { supportsChildLock } from '../../../../../core/devices/childLock';
import { backTargetFor } from '../../../../../core/devices/deviceNav';

export default function DeviceAppBar({
  device, extraItems = [], onRenamed, subtitle: subtitleOverride,
}) {
  const theme = useTheme();
  const c = theme.sli;
  const navigate = useNavigate();
  // Set by whichever screen opened this device — it knows the room AND the
  // location, which the device row on its own often does not.
  const { state: navState } = useLocation();
  const { userId, canUpdateFirmware } = useApp();
  const { show } = useMessage();

  const [anchor, setAnchor] = useState(null);
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState('');
  const [infoOpen, setInfoOpen] = useState(false);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [wifiOpen, setWifiOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  const [childLockOpen, setChildLockOpen] = useState(false);
  // ── Per-button settings, for EVERY device type ───────────────────────────
  // It was reachable on the phone only from TouchScreen's own tiles, so the
  // outputs of a fan, a light, a curtain or a door had no way to reach their
  // name, All On/All Off behaviour, power-restore state or two-way mapping —
  // all of which exist for them on `OutputDetails` exactly as for a touch
  // panel. Desktop has always offered it for every type through its Settings
  // dialog. Living on the app bar means one implementation covers every device
  // screen at once, including any added later.
  //
  // `null` = closed. A number is the 1-based output being edited.
  const [buttonOutput, setButtonOutput] = useState(null);
  const [pickOutputOpen, setPickOutputOpen] = useState(false);
  // ⚠️ A device with no outputs has no buttons to configure — a sensor and an
  // IR blaster both report 0, and the dialog would open on a row that does not
  // exist. See core/devices/sensorState for why Outputs===0 is not itself the
  // sensor test; here it is exactly the right question.
  const outputCount = Number(device?.Outputs) || 0;
  // The SSID the device reports — see the note where it is fetched.
  const [wifiName, setWifiName] = useState(null);
  // undefined = still resolving, null = could not be resolved.
  const [wifiLocationId, setWifiLocationId] = useState(undefined);

  const uno = device?.UniqueNumber;
  const title = device?.DeviceName || `Unnamed (${device?.DeviceTypeName ?? ''})`;

  // ── The SSID the device is ACTUALLY joined to ────────────────────────────
  //
  // ⚠️ NOT `GetDeviceDetailbyUniqueNumber`, and not the location's configured
  // network either. That query names its columns (`a.ID, a.MasterUserId, … `)
  // and `WifiName` is not among them, so the SSID is invisible there — which is
  // why an earlier version of this sheet showed `Locations.WifiName` and called
  // it "Site Wi-Fi". That was the wrong fact: the site's planned network, not
  // where this device went.
  //
  // `GetDeviceInstalledDetailsbyDevicesInstalledId` is `select a.*`, so it has
  // the whole row — including the SSID the FIRMWARE writes on every status
  // update (device_api.c → UpdateStatusByUniqueNumber with data.WifiName). It
  // takes the INSTALLED id, so the device's own row has to be resolved first
  // when this screen was opened from a list, which carries no `ID`.
  //
  // ⚠️ The reply also contains `WifiPassword` in clear. Only WifiName is kept —
  // never put the whole row into state where a log or a crash reporter can see
  // it.
  //
  // Runs only when the sheet opens, and fails silently: one line of detail is
  // not worth an error state.
  useEffect(() => {
    if (!infoOpen) return undefined;
    let alive = true;
    (async () => {
      try {
        let installedId = device?.ID;
        if (installedId == null) {
          const res = await getDeviceDetail(uno);
          installedId = (Array.isArray(res.data) ? res.data[0] : null)?.ID;
        }
        if (installedId == null) { if (alive) setWifiName(null); return; }
        const res = await getDeviceInstalledById(installedId);
        const row = Array.isArray(res.data) ? res.data[0] : null;
        if (alive) setWifiName(row?.WifiName || null);
      } catch (e) {
        if (alive) setWifiName(null);
      }
    })();
    return () => { alive = false; };
  }, [infoOpen, device, uno]);

  // ⚠️ `viaBack` IS WHAT STOPS THE DRILL SENDING US STRAIGHT BACK HERE. The
  // list we return to may hold exactly one thing — that is how we got here in
  // the first place — and without the flag it would open it again on arrival,
  // leaving no way off this screen at all. See core/nav/appNav.js.
  const goBack = () => {
    const target = backTargetFor(device, navState?.from);
    if (target) navigate(target.path, { state: { ...target.state, viaBack: true } });
    else navigate(-1);
  };
  // Most Flutter device screens show "Location > Room"; the phase meters show the
  // location alone, so they pass their own subtitle.
  const subtitle = subtitleOverride
    ?? `${device?.LocationName ?? ''} > ${device?.RoomName ?? ''}`;

  const submitRename = async () => {
    const name = newName.trim();
    if (!name) { show('Invalid Name', 'Device name cannot be empty.'); return; }
    if (name.length > 50) {
      show('Invalid Name', 'Device name cannot exceed 50 characters.');
      return;
    }
    try {
      await renameDeviceInstalled(uno, name);
      setRenaming(false);
      show('Name Updated', `Device name updated successfully to "${name}".`);
      if (onRenamed) onRenamed(name);
    } catch (e) {
      show('Update Failed',
        'An error occurred while updating the device name. Please check your network and try again.');
    }
  };

  const wifiThen = (fn) => () => {
    setAnchor(null);
    // Flutter refreshes the device's WiFi list before opening a WiFi entry.
    publishCommand(uno, CMD_GET_ALL_WIFI, userId);
    fn();
  };

  // The device's location, needed to offer the networks already in use there.
  //
  // ⚠️ NOT taken from the device row: the list endpoints return LocationName but
  // no LocationID. GetDeviceDetailbyUniqueNumber has it.
  const openAddWifi = async () => {
    setWifiOpen(true);
    setWifiLocationId(undefined);
    const res = await getDeviceDetail(uno).catch(() => null);
    const row = Array.isArray(res?.data) ? res.data[0] : null;
    setWifiLocationId(row ? (row.LocationID ?? row.LocationId ?? null) : null);
  };

  // ADDWIFI / REMOVEWIFI are the one pair the firmware does NOT upper-case,
  // because an SSID and a password are case-sensitive. buildAddWifiMqtt is the
  // only place that literal is written.
  //
  // ⚠️ publishAddressed, NOT publishCommand: the `#<userId>` suffix is not
  // stripped by the ESP8266 for these two verbs and ends up glued to the
  // password, so the device stores a credential it can never join with. See
  // core/devices/commands.js.
  const sendAddWifi = ({ ssid, pass }) => {
    publishAddressed(uno, buildAddWifiMqtt(ssid, pass));
    setWifiOpen(false);
    show('Wi-Fi Sent', `"${ssid}" was sent to this device. It restarts to apply it, `
      + 'so it drops offline for a few seconds.');
  };

  const items = [
    {
      icon: <InfoIcon fontSize="small" />,
      title: 'Device Details',
      onClick: () => { setAnchor(null); setInfoOpen(true); },
    },
    {
      icon: <SignalWifi4BarIcon fontSize="small" />,
      title: 'Add WiFi',
      onClick: wifiThen(openAddWifi),
    },
    {
      icon: <SignalWifiBadSharpIcon fontSize="small" />,
      title: 'Remove WiFi',
      onClick: wifiThen(() => setRemoveOpen(true)),
    },
    // Touch panels only — a device with no keypad has nothing to lock. See
    // core/devices/childLock.js for the list and for why the 4+1 is not in it.
    ...(supportsChildLock(device?.DeviceID) ? [{
      icon: <LockIcon fontSize="small" />,
      title: 'Child Lock',
      onClick: () => { setAnchor(null); setChildLockOpen(true); },
    }] : []),
    // One output goes straight in; several ask which. A picker in front of a
    // single choice is a tap that answers nothing.
    ...(outputCount > 0 ? [{
      icon: <TuneIcon fontSize="small" />,
      title: 'Button Settings',
      onClick: () => {
        setAnchor(null);
        if (outputCount === 1) setButtonOutput(1);
        else setPickOutputOpen(true);
      },
    }] : []),
    // Firmware push for this one device — owner / super admin only.
    ...(canUpdateFirmware ? [{
      icon: <SystemUpdateAltIcon fontSize="small" />,
      title: 'Update Code',
      onClick: () => { setAnchor(null); setUpdateOpen(true); },
    }] : []),
    ...extraItems,
  ];

  return (
    <>
      <ProfileTitleBar
        title={title}
        subtitle={subtitle}
        // Back retraces the STRUCTURE — Location → Room → Devices → Device —
        // not the history stack. Falls back to history only when the device
        // knows neither its room nor its location.
        onBack={goBack}
        actions={(
          <>
            <IconButton
              onClick={() => { setNewName(device?.DeviceName || ''); setRenaming(true); }}
              sx={{ color: c.text }}
            >
              <EditIcon />
            </IconButton>
            <IconButton onClick={(e) => setAnchor(e.currentTarget)} sx={{ color: c.text }}>
              <MoreVertIcon />
            </IconButton>
          </>
        )}
      />

      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        PaperProps={{ sx: { bgcolor: c.button, borderRadius: '16px' } }}
      >
        {items.map((it) => (
          <MenuItem key={it.title} onClick={it.onClick}>
            <ListItemIcon sx={{ color: c.text, minWidth: 32 }}>{it.icon}</ListItemIcon>
            <ListItemText
              primary={it.title}
              primaryTypographyProps={{ sx: { color: c.text, fontWeight: 700 } }}
            />
          </MenuItem>
        ))}
      </Menu>

      {/* allDeviceFunctions.dart promptForDeviceName() */}
      <Dialog open={renaming} onClose={() => setRenaming(false)}
        PaperProps={{ sx: { bgcolor: c.back, borderRadius: '20px', width: 'min(80vw, 400px)' } }}>
        <DialogTitle sx={{ color: c.text, fontWeight: 700, textAlign: 'center', fontSize: 20 }}>
          Enter New Device Name
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth placeholder="Type here..."
            value={newName} onChange={(e) => setNewName(e.target.value)}
            sx={{ mt: 1, '& .MuiInputBase-input': { color: c.text } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenaming(false)} sx={{ color: c.text }}>Cancel</Button>
          <Button variant="contained" onClick={submitRename}
            sx={{ bgcolor: c.button, color: '#fff', borderRadius: '12px' }}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* devices/infoScreen.dart, condensed to a sheet. */}
      <Dialog open={infoOpen} onClose={() => setInfoOpen(false)}
        PaperProps={{ sx: { bgcolor: c.back, borderRadius: '16px', minWidth: 300 } }}>
        <DialogTitle sx={{ color: c.text, fontWeight: 700 }}>Device Details</DialogTitle>
        <DialogContent>
          {[
            ['Name', device?.DeviceName],
            ['Type', deviceLabel(device?.DeviceID)],
            ['Device ID', device?.DeviceID],
            ['Unique Number', uno],
            ['Location', device?.LocationName],
            ['Room', device?.RoomName],
            // The network the DEVICE reports it is joined to — the firmware
            // sends it with every status update, so it is as fresh as the last
            // thing the device said. Not the location's configured SSID.
            ['Wi-Fi', wifiName],
            ['IP Address', device?.DeviceIp],
          ].map(([k, v]) => (
            <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, gap: 3 }}>
              <Typography sx={{ color: `${c.text}B3` }}>{k}</Typography>
              <Typography sx={{ color: c.text, fontWeight: 600 }}>{v ?? '—'}</Typography>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)} sx={{ color: c.text }}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={childLockOpen} onClose={() => setChildLockOpen(false)}
        PaperProps={{ sx: { bgcolor: c.back, borderRadius: '16px', minWidth: 300 } }}>
        <DialogTitle sx={{ color: c.text, fontWeight: 700 }}>Child Lock</DialogTitle>
        <DialogContent>
          <ChildLockPanel device={device} dense />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setChildLockOpen(false)} sx={{ color: c.text }}>Close</Button>
        </DialogActions>
      </Dialog>

      <UpdateCodeDialog
        open={updateOpen}
        scope={{ type: 'device', device, name: device?.DeviceName || uno }}
        onClose={() => setUpdateOpen(false)}
      />

      {/* Keyed on the resolved location so the picker mounts once the id is
          known and loads the right site's saved networks. */}
      <WifiPickerDialog
        key={`wifi-${wifiLocationId}`}
        open={wifiOpen}
        locationId={wifiLocationId === undefined ? null : wifiLocationId}
        deviceName={title}
        submitLabel="Add Wi-Fi"
        onClose={() => setWifiOpen(false)}
        onSubmit={sendAddWifi}
      />

      <WifiRemoveDialog open={removeOpen} uno={uno} onClose={() => setRemoveOpen(false)} />

      {/* Which button. Plain 1..n: OutputDetails is addressed by output number
          and the row's own name is what this dialog edits, so naming them here
          from a table would be one more place for the two to disagree. */}
      <Dialog
        open={pickOutputOpen}
        onClose={() => setPickOutputOpen(false)}
        PaperProps={{ sx: { bgcolor: c.back, borderRadius: '20px', minWidth: 260 } }}
      >
        <DialogTitle sx={{ color: c.text, fontWeight: 700 }}>Which button?</DialogTitle>
        <DialogContent sx={{ pb: 1 }}>
          {Array.from({ length: outputCount }, (_, i) => i + 1).map((n) => (
            <Button
              key={n}
              fullWidth
              onClick={() => { setPickOutputOpen(false); setButtonOutput(n); }}
              sx={{ justifyContent: 'flex-start', color: c.text, py: 1.25 }}
            >
              {`Button ${n}`}
            </Button>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPickOutputOpen(false)} sx={{ color: c.text }}>Cancel</Button>
        </DialogActions>
      </Dialog>

      <ButtonSettingsDialog
        open={buttonOutput != null}
        uniqueNumber={uno}
        output={buttonOutput ?? 1}
        onClose={() => setButtonOutput(null)}
        onOpenSchedule={() => {
          const output = buttonOutput;
          setButtonOutput(null);
          navigate(`/schedule/${uno}/${output}`);
        }}
      />
    </>
  );
}
